import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Repeat, PauseCircle, PlayCircle, XCircle, Zap, Phone, MapPin, BellRing } from "lucide-react";
import api from "@/lib/api";
import { formatJMD, fmtDate, PAYMENT_LABEL } from "@/lib/format";
import { WEEKDAYS, FREQ_LABEL, SUB_STATUS } from "@/pages/SubscribePage";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export const StandingOrdersTab = ({ reload }) => {
  const [subs, setSubs] = useState([]);
  const [filter, setFilter] = useState("active");
  const [running, setRunning] = useState(false);
  const [reminding, setReminding] = useState(false);

  const load = useCallback(() => api.get("/subscriptions").then((r) => setSubs(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const act = async (s, action) => {
    try {
      await api.patch(`/subscriptions/${s.id}/action`, { action });
      toast.success(`${s.code} ${action}d`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/subscriptions/run-now");
      toast.success(`${data.created} order(s) created from due standing orders`);
      load(); reload();
    } catch { toast.error("Run failed"); } finally { setRunning(false); }
  };

  const sendReminders = async () => {
    setReminding(true);
    try {
      const { data } = await api.post("/subscriptions/send-reminders-now");
      toast.success(`${data.sent} reminder email(s) sent for tomorrow's deliveries`);
    } catch { toast.error("Reminder run failed"); } finally { setReminding(false); }
  };

  const shown = filter === "all" ? subs : subs.filter((s) => s.status === filter);
  const counts = subs.reduce((a, s) => ({ ...a, [s.status]: (a[s.status] || 0) + 1 }), {});

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex gap-2">
          {["active", "paused", "cancelled", "all"].map((f) => (
            <button key={f} data-testid={`subs-filter-${f}`} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize ${filter === f ? "bg-sky-600 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
              {f} {f !== "all" && counts[f] ? `(${counts[f]})` : ""}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <Button data-testid="subs-send-reminders" onClick={sendReminders} disabled={reminding} variant="outline" className="rounded-xl">
            <BellRing className="w-4 h-4 mr-1.5 text-sky-500" /> Send tomorrow's reminders
          </Button>
          <Button data-testid="subs-run-now" onClick={runNow} disabled={running} variant="outline" className="rounded-xl">
            <Zap className="w-4 h-4 mr-1.5 text-amber-500" /> Generate due deliveries now
          </Button>
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-4">Deliveries are created automatically every morning at 6:00am (Jamaica time) for standing orders due that day. Customers with an email get a reminder at 6:00pm the evening before to have empties ready.</p>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="subs-list">
        {shown.map((s) => (
          <div key={s.id} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`sub-card-${s.id}`}>
            <div className="flex items-center justify-between">
              <span className="font-mono-cw font-bold text-sky-700">{s.code}</span>
              <Badge className={`border text-[10px] capitalize ${SUB_STATUS[s.status]}`}>{s.status}</Badge>
            </div>
            <div className="font-semibold text-slate-900 mt-2">{s.customer_name}</div>
            <div className="text-xs text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3" /> {s.phone}</div>
            <div className="text-xs text-slate-500 flex items-center gap-1 truncate"><MapPin className="w-3 h-3" /> {s.address}</div>
            <div className="mt-3 text-sm text-slate-700 flex items-center gap-1.5"><Repeat className="w-4 h-4 text-sky-600" /> {s.quantity}× {s.product_name}</div>
            <div className="text-xs text-slate-500">{FREQ_LABEL[s.frequency]} · {WEEKDAYS[s.weekday]}s · {PAYMENT_LABEL[s.payment_method]}</div>
            <div className="flex items-end justify-between mt-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400">{s.status === "active" ? "Next delivery" : "Deliveries so far"}</div>
                <div className="font-semibold text-slate-800 text-sm">{s.status === "active" ? fmtDate(s.next_delivery) : s.deliveries_count}</div>
              </div>
              <div className="font-mono-cw font-bold text-slate-900">{formatJMD(s.unit_price * s.quantity)}</div>
            </div>
            {s.status !== "cancelled" && (
              <div className="flex gap-1 mt-4 pt-3 border-t border-slate-100">
                {s.status === "active" && <Button size="sm" variant="ghost" onClick={() => act(s, "pause")} data-testid={`sub-pause-${s.id}`} className="h-8 text-xs text-amber-700"><PauseCircle className="w-3.5 h-3.5 mr-1" /> Pause</Button>}
                {s.status === "paused" && <Button size="sm" variant="ghost" onClick={() => act(s, "resume")} data-testid={`sub-resume-${s.id}`} className="h-8 text-xs text-emerald-700"><PlayCircle className="w-3.5 h-3.5 mr-1" /> Resume</Button>}
                <Button size="sm" variant="ghost" onClick={() => act(s, "cancel")} data-testid={`sub-cancel-${s.id}`} className="h-8 text-xs text-rose-500 ml-auto"><XCircle className="w-3.5 h-3.5 mr-1" /> Cancel</Button>
              </div>
            )}
          </div>
        ))}
        {shown.length === 0 && <div className="col-span-full bg-white rounded-2xl border border-dashed border-slate-300 p-12 text-center text-slate-400">No {filter !== "all" ? filter : ""} standing orders.</div>}
      </div>
    </div>
  );
};
